import React, { useState, useEffect } from "react";
import { Icon } from "../../UIElements/Icon";
import { useGlobalUtils } from "../../../../lib/globalUtils";
import { useTranslation } from "react-i18next";

/**
 * OrderNotificationToast Component
 * Displays real-time order notifications with smooth animations
 */
const OrderNotificationToast = ({
  order,
  isVisible,
  onClose,
  duration = 5000,
  position = "top-right",
  type = "new",
}) => {
  const { t } = useTranslation("orders");
  const { parsePrice } = useGlobalUtils();

  const [isAnimating, setIsAnimating] = useState(false);
  const [timeLeft, setTimeLeft] = useState(duration / 1000);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      setTimeLeft(duration / 1000);

      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      // Update countdown every 100ms for smooth animation
      const countdownInterval = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = prev - 0.1;
          return newTime <= 0 ? 0 : newTime;
        });
      }, 100);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    }
  }, [isVisible, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  if (!isVisible || !order) return null;

  const positionClasses = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
  };

  return (
    <div
      className={`fixed ${positionClasses[position]} z-50 transform transition-all duration-300 ease-in-out ${
        isAnimating
          ? "translate-x-0 scale-100 opacity-100"
          : "translate-x-full scale-95 opacity-0"
      }`}
    >
      <div className="relative w-72 max-w-sm overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-2xl backdrop-blur-sm">
        <div className="bg-brand-gradient absolute inset-0 rounded-lg opacity-10"></div>
        <div className="bg-brand-gradient absolute inset-0 -z-10 rounded-lg opacity-20 blur-sm"></div>

        {/* Header */}
        <div className="relative flex items-center justify-between border-b border-gray-700/50 p-3">
          <div className="flex items-center space-x-2">
            <div className="bg-brand-gradient relative rounded-lg p-1.5 shadow-lg">
              <div className="bg-brand-gradient absolute inset-0 rounded-lg opacity-30 blur-sm"></div>
              <Icon
                name="ClockArrowDown"
                className="relative h-4 w-4 text-white drop-shadow-sm"
              />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white drop-shadow-sm">
                {type === "new"
                  ? t("notifications.newOrder")
                  : t("notifications.orderUpdated")}{" "}
                #{order?.id}
              </h4>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="group relative rounded-md p-1.5 transition-all duration-200 hover:bg-white/10"
          >
            <Icon
              name="X"
              className="h-3.5 w-3.5 text-gray-300 transition-colors group-hover:text-white"
            />
          </button>
        </div>

        {/* Content */}
        <div className="relative p-3">
          <div className="space-y-2.5">
            {/* Order Details */}
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-2.5 py-2">
                <span className="text-xs text-gray-300">
                  {t("notifications.total")}
                </span>
                <span className="rounded-full bg-green-400/10 px-2 py-0.5 text-sm font-semibold text-green-400">
                  {parsePrice(order.summary?.total ?? 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Progress Bar */}
        <div className="absolute right-0 bottom-0 left-0 h-1 overflow-hidden rounded-b-lg bg-white/10">
          <div
            className="bg-brand-gradient absolute top-0 left-0 h-full transition-all duration-100 ease-linear"
            style={{
              width: `${(timeLeft / (duration / 1000)) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderNotificationToast;
